import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import {
  NumerolojiMeditationEditor,
  type MeditationDraft,
} from "@/components/admin/NumerolojiMeditationEditor";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Bell } from "lucide-react";

type CatalogMeditation = {
  id: string;
  title: string;
  description: string;
  durationMinutes: number;
  level: "beginner" | "intermediate" | "advanced";
  coverImageUrl?: string;
  audioUrl?: string;
  active: boolean;
  isPremium?: boolean;
  tags: string[];
  content_language?: string;
  translation_group_id?: string;
};

type MeditationsResponse = {
  ok: true;
  items: CatalogMeditation[];
};

type ContentMode = "meditation" | "breath";

function normalizeTag(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

const PROGRAM_DAYS_TAG_PREFIX = "program_gun:";

function parseProgramDaysFromTags(tags: string[]) {
  for (const tag of tags ?? []) {
    const raw = String(tag ?? "").trim();
    const match = raw.match(/^program_gun:\s*(\d{1,3})$/i);
    const parsed = Number(match?.[1] ?? "");
    if (Number.isFinite(parsed) && parsed > 0) {
      return String(Math.round(parsed));
    }
  }
  return "";
}

function stripProgramDaysTag(tags: string[]) {
  return (tags ?? []).filter((tag) => !/^program_gun:\s*\d{1,3}$/i.test(String(tag ?? "").trim()));
}

function buildProgramDaysTag(programDays?: string | number) {
  const parsed = Number(programDays);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return `${PROGRAM_DAYS_TAG_PREFIX}${Math.round(parsed)}`;
}

const XP_TAG_PREFIX = "xp:";

function parseXpFromTags(tags: string[]) {
  for (const tag of tags ?? []) {
    const raw = String(tag ?? "").trim();
    const match = raw.match(/^xp:\s*(\d{1,4})$/i);
    const parsed = Number(match?.[1] ?? "");
    if (Number.isFinite(parsed) && parsed > 0) return String(Math.round(parsed));
  }
  return "";
}

function stripXpTag(tags: string[]) {
  return (tags ?? []).filter((tag) => !/^xp:\s*\d{1,4}$/i.test(String(tag ?? "").trim()));
}

function buildXpTag(xpPoints?: string | number) {
  const parsed = Number(xpPoints);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return `${XP_TAG_PREFIX}${Math.round(parsed)}`;
}

function ensureBreathTags(tagsText: string) {
  const parts = tagsText
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  const hasNefes = parts.some((part) => normalizeTag(part) === "nefes");
  return hasNefes ? parts.join(", ") : ["nefes", ...parts].join(", ");
}

function getMeditationApiBase(mode: ContentMode) {
  return mode === "breath" ? "/api/admin/numeroloji/breaths" : "/api/admin/numeroloji/meditations";
}

async function fetchMeditations(mode: ContentMode): Promise<CatalogMeditation[]> {
  const res = await adminFetch(getMeditationApiBase(mode));
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    const fallback = mode === "breath" ? "Nefes seansları alınamadı." : "Meditasyonlar alınamadı.";
    throw new Error(typeof json?.error === "string" ? json.error : fallback);
  }
  const data = json as MeditationsResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

async function saveMeditation(mode: ContentMode, payload: Partial<CatalogMeditation> & { title: string }) {
  const res = await adminFetch(getMeditationApiBase(mode), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    const fallback = mode === "breath" ? "Nefes seansı kaydedilemedi." : "Meditasyon kaydedilemedi.";
    throw new Error(typeof json?.error === "string" ? json.error : fallback);
  }
  return json as { ok: true; item: CatalogMeditation };
}

function emptyDraft(mode: ContentMode, initLang: 'tr' | 'en' | 'es' = 'tr'): MeditationDraft {
  return {
    title: "",
    description: "",
    durationMinutes: 10,
    level: "beginner",
    programDays: "",
    xpPoints: "",
    coverImageUrl: "",
    audioUrl: "",
    active: true,
    isPremium: false,
    tagsText: mode === "breath" ? "nefes, genel" : "genel",
    price: 0,
    store_product_id: null,
    content_language: initLang,
  };
}

export default function AdminNumerolojiMeditation({ mode = "meditation" }: { mode?: ContentMode } = {}) {
  const params = useParams();
  const meditationId = params.id ? String(params.id).trim() : "";
  const isNew = !meditationId;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const initLang = (() => {
    const p = searchParams.get('lang');
    return (p === 'en' || p === 'es') ? p : 'tr';
  })();

  const groupIdParam = searchParams.get('groupId') ?? null;

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const isBreathMode = mode === "breath";
  const basePath = isBreathMode ? "/admin/numeroloji/nefes" : "/admin/numeroloji/meditasyon";
  const isPersistedStorage = sessionQuery.data?.numerolojiStorage === "supabase";

  const meditationsQuery = useQuery({
    queryKey: ["admin-numeroloji-meditations", mode],
    queryFn: () => fetchMeditations(mode),
    enabled: authenticated,
    staleTime: 30_000,
  });

  const meditations = useMemo(() => {
    const rows = meditationsQuery.data ?? [];
    return rows;
  }, [meditationsQuery.data]);

  const currentGroupId = useMemo(() => {
    if (isNew) return groupIdParam;
    const item = meditations.find((m) => m.id === meditationId);
    return item?.translation_group_id ?? null;
  }, [isNew, groupIdParam, meditations, meditationId]);

  const siblings = useMemo(() => {
    if (!currentGroupId) return [];
    return meditations
      .filter((m) => m.translation_group_id === currentGroupId)
      .map((m) => ({ id: m.id, content_language: m.content_language ?? 'tr' }));
  }, [currentGroupId, meditations]);

  const [draft, setDraft] = useState<MeditationDraft>(() => emptyDraft(mode, initLang));
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    setSeeded(false);
  }, [authenticated, isNew, meditationId, mode]);

  useEffect(() => {
    if (!authenticated) return;

    if (isNew) {
      if (!seeded) {
        // If creating a new version in a group, wait for meditations to load so we can copy the cover
        if (groupIdParam && !meditationsQuery.data) return;
        const base = emptyDraft(mode, initLang);
        if (groupIdParam) {
          const sibling = meditations.find((m) => m.translation_group_id === groupIdParam);
          if (sibling?.coverImageUrl) {
            base.coverImageUrl = sibling.coverImageUrl;
          }
        }
        setDraft(base);
        setSeeded(true);
      }
      return;
    }

    if (!meditationsQuery.data || seeded) return;

    const item = meditations.find((m) => m.id === meditationId);
    if (!item) {
      setSeeded(true);
      return;
    }

    const programDays = parseProgramDaysFromTags(item.tags ?? []);
    const xpPoints = parseXpFromTags(item.tags ?? []);
    const visibleTags = stripXpTag(stripProgramDaysTag(item.tags ?? []));

    setDraft({
      id: item.id,
      title: item.title,
      description: item.description,
      durationMinutes: item.durationMinutes,
      level: item.level,
      programDays,
      xpPoints,
      coverImageUrl: item.coverImageUrl ?? "",
      audioUrl: item.audioUrl ?? "",
      active: item.active,
      isPremium: item.isPremium ?? false,
      tagsText: visibleTags.join(", "),
      price: (item as any).price ?? 0,
      store_product_id: (item as any).store_product_id ?? null,
      music_url: (item as any).music_url ?? "",
      music_volume: (item as any).music_volume ?? 30,
      kategori_id: (item as any).kategori_id != null ? String((item as any).kategori_id) : '',
      content_language: (item as any).content_language ?? 'tr',
    });
    setSeeded(true);
  }, [authenticated, isNew, meditationId, meditations, meditationsQuery.data, mode, seeded]);

  const mutation = useMutation({
    mutationFn: (payload: any) => {
      const baseTags = stripXpTag(stripProgramDaysTag(Array.isArray(payload?.tags) ? payload.tags : []));
      const programDaysTag = buildProgramDaysTag(payload?.programDays);
      const xpTag = buildXpTag(payload?.xpPoints);
      const finalTags = [
        ...baseTags,
        ...(programDaysTag ? [programDaysTag] : []),
        ...(xpTag ? [xpTag] : []),
      ];
      return saveMeditation(mode, { ...payload, tags: finalTags });
    },
    onSuccess: (data) => {
      const returnedId = (data as any)?.item?.id;
      if (returnedId) {
        setDraft((prev: any) => ({ ...prev, id: returnedId }));
      }
      void queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-meditations"] });
      navigate(basePath, { replace: true });
    },
  });

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  if (!isNew && meditationsQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!isNew && meditationsQuery.isError) {
    return <div className="text-sm text-destructive">{(meditationsQuery.error as Error).message}</div>;
  }

  if (!isNew && meditationsQuery.data && !meditations.some((m) => m.id === meditationId)) {
    return (
      <div className="p-4 sm:p-6">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {isBreathMode ? "Nefes seansı bulunamadı." : "Meditasyon bulunamadı."}
        </div>
        <div className="mt-4">
          <Button
            className="rounded-full"
            onClick={() => navigate(basePath, { replace: true })}
          >
            {isBreathMode ? "Nefes seanslarına dön" : "Meditasyonlara dön"}
          </Button>
        </div>
      </div>
    );
  }

  const headerTitle = isBreathMode
    ? isNew
      ? "Yeni Nefes Seansı"
      : "Nefes Seansı Düzenle"
    : isNew
      ? "Yeni Meditasyon"
      : "Meditasyon Düzenle";

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                {headerTitle}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        {sessionQuery.data?.numerolojiStorage === "memory" ? (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100 sm:px-6">
            Uyarı: Veri tabanı bağlantısı sunucuda aktif değil. Meditasyon/Nefes kayıtları kalıcı kaydedilmez ve sunucu
            yenilenince kaybolabilir. (Sunucuda SUPABASE_URL + SUPABASE_SERVICE_ROLE_KEY gerekli.)
          </div>
        ) : null}

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <NumerolojiMeditationEditor
            draft={draft}
            setDraft={setDraft}
            isSaving={mutation.isPending}
            saveDisabled={!isPersistedStorage}
            saveLabel={!isPersistedStorage ? "Veritabanı gerekli" : undefined}
            mode={mode}
            siblings={siblings}
            coverPrefilledFromSibling={isNew && Boolean(groupIdParam)}
            onClose={() => navigate(basePath, { replace: true })}
            onNavigateSibling={(id) => navigate(`${basePath}/${id}`)}
            onCreateVersion={(lang) => {
              const gid = currentGroupId ?? (draft as any).translation_group_id ?? '';
              navigate(`${basePath}/yeni?lang=${lang}${gid ? `&groupId=${gid}` : ''}`);
            }}
            onSave={(payload) => {
              if (!isPersistedStorage) return;

              const baseTags = stripProgramDaysTag(Array.isArray(payload?.tags) ? payload.tags : []);
              const programDaysTag = buildProgramDaysTag(payload?.programDays);
              const tagsWithProgram = programDaysTag ? [...baseTags, programDaysTag] : baseTags;

              if (!isBreathMode) {
                mutation.mutate({
                  ...payload,
                  contentMode: mode,
                  tags: tagsWithProgram,
                  ...(isNew && currentGroupId ? { parentGroupId: currentGroupId } : {}),
                });
                return;
              }

              mutation.mutate({
                ...payload,
                contentMode: mode,
                tagsText: ensureBreathTags(String(payload?.tagsText ?? "")),
                tags: Array.from(
                  new Set(
                    ["nefes", ...tagsWithProgram]
                      .map((tag) => String(tag ?? "").trim())
                      .filter(Boolean),
                  ),
                ),
                ...(isNew && currentGroupId ? { parentGroupId: currentGroupId } : {}),
              });
            }}
          />

          {!isPersistedStorage ? (
            <div className="mt-4 rounded-2xl border border-amber-300/40 bg-amber-50/70 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
              Kalıcı kayıt için sunucu ortam değişkenlerinde Supabase servis anahtarı tanımlanmalı.
            </div>
          ) : mutation.isError ? (
            <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {(mutation.error as Error).message}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
